// src/hooks/useJanetVoice.js — Per-session voice toggle for Janet TTS.
// Default: off. Principal opts in once per session; state survives page navigation.
//
// API:
//   speak(text)             — one-shot: speak a complete utterance now.
//   speakChunk(text, opts)  — streaming: append text to a sentence buffer.
//                             Each completed sentence fires TTS in parallel and
//                             enters a sequential audio playback queue.
//   flushSpeak()            — speak any text remaining in the buffer (final
//                             partial sentence) and resolve when all queued
//                             audio has finished playing.
//   stop()                  — abort everything in flight + clear queue.
//
// Failures degrade silently to text-only — voice is never load-bearing.

import { useState, useCallback, useRef } from 'react'

const SESSION_KEY = 'chatrone.janet.voice.enabled'

function readEnabled() {
  try { return sessionStorage.getItem(SESSION_KEY) === 'true' } catch { return false }
}
function writeEnabled(v) {
  try { sessionStorage.setItem(SESSION_KEY, String(v)) } catch { /* sessionStorage unavailable */ }
}

// Split text into sentences. Conservative — splits only on sentence-ending
// punctuation followed by whitespace. Doesn't try to handle abbreviations
// (Mr., e.g., etc.) since occasional over-splitting just creates a tiny
// extra TTS call, while under-splitting forces longer waits.
function splitSentences(text) {
  const cleaned = String(text || '').replace(/\s+/g, ' ').trim()
  if (!cleaned) return []
  // Split after .!? when followed by space and uppercase/quote (heuristic).
  const parts = cleaned.split(/(?<=[.!?])\s+(?=["'([A-Z])/g).filter(Boolean)
  return parts
}

// Find the longest prefix of `buf` that ends at a sentence boundary.
// Returns [completedPrefix, remainder]. If no boundary, returns ['', buf].
function pullCompleteSentences(buf) {
  // Match all sentence ends in the buffer.
  const matches = [...buf.matchAll(/[.!?](?:\s+|$)/g)]
  if (matches.length === 0) return ['', buf]
  const last = matches[matches.length - 1]
  const cutAt = last.index + last[0].length
  if (cutAt >= buf.length) return [buf.trim(), '']
  return [buf.slice(0, cutAt).trim(), buf.slice(cutAt)]
}

export function useJanetVoice() {
  const [voiceEnabled, setVoiceEnabledState] = useState(readEnabled)
  const [isSpeaking, setIsSpeaking] = useState(false)

  // Streaming-state refs (don't trigger re-renders)
  const sentenceBufferRef = useRef('')             // accumulating partial sentence
  const fetchAbortRef     = useRef(null)           // AbortController for in-flight fetches
  const audioQueueRef     = useRef([])             // [{ promise: Promise<Blob|null> }]
  const playbackChainRef  = useRef(Promise.resolve()) // sequential playback chain
  const currentAudioRef   = useRef(null)
  const stopFlagRef       = useRef({ cancelled: false })

  const stopCurrent = useCallback(() => {
    // Cancel any in-flight TTS fetches
    if (fetchAbortRef.current) {
      try { fetchAbortRef.current.abort() } catch { /* already aborted */ }
      fetchAbortRef.current = null
    }
    // Mark current chain cancelled so each playback step bails out
    stopFlagRef.current.cancelled = true
    stopFlagRef.current = { cancelled: false }
    // Stop any audio currently playing
    if (currentAudioRef.current) {
      try { currentAudioRef.current.pause() } catch { /* not playing */ }
      currentAudioRef.current = null
    }
    // Reset queues + buffer
    audioQueueRef.current = []
    sentenceBufferRef.current = ''
    playbackChainRef.current = Promise.resolve()
    setIsSpeaking(false)
  }, [])

  const toggleVoice = useCallback(() => {
    setVoiceEnabledState(v => {
      const next = !v
      writeEnabled(next)
      if (!next) stopCurrent()
      return next
    })
  }, [stopCurrent])

  // ── Internal: fetch TTS for one sentence, return Blob (or null on failure) ──
  // Each sentence gets its own AbortController with an 8s timeout so a slow
  // Cartesia response never hangs the playback chain indefinitely.
  const fetchTTS = useCallback((text) => {
    const ctrl = new AbortController()
    // Store latest controller so stopCurrent() can still cancel in-flight fetches
    fetchAbortRef.current = ctrl
    const timeout = setTimeout(() => ctrl.abort(), 60000)
    return fetch('/api/janet-speak', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
      signal: ctrl.signal,
    })
      .then(async res => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          console.warn('[Janet TTS] API error', res.status, body?.error || '')
          return null
        }
        return res.blob()
      })
      .catch(err => {
        if (err?.name !== 'AbortError') console.warn('[Janet TTS] fetch failed:', err?.message)
        return null
      })
      .finally(() => clearTimeout(timeout))
  }, [])

  // ── Internal: enqueue a sentence — kicks off TTS and chains playback ────────
  // onStart (optional): called exactly once when audio.play() resolves, or on
  // any failure path (blob null, audio error, abort) so callers are never
  // left waiting forever for a reveal that won't come.
  const enqueueSentence = useCallback((text, onStart) => {
    if (!text?.trim()) return
    setIsSpeaking(true)

    const stopFlag = stopFlagRef.current
    const blobPromise = fetchTTS(text)
    let startFired = false
    const fireStart = () => { if (!startFired) { startFired = true; onStart?.() } }

    playbackChainRef.current = playbackChainRef.current.then(async () => {
      if (stopFlag.cancelled) { fireStart(); return }
      const blob = await blobPromise
      if (stopFlag.cancelled || !blob) { fireStart(); return }

      const url = URL.createObjectURL(blob)
      const audio = new Audio(url)
      currentAudioRef.current = audio

      await new Promise(resolve => {
        const cleanup = () => {
          URL.revokeObjectURL(url)
          if (currentAudioRef.current === audio) currentAudioRef.current = null
          resolve()
        }
        audio.onended = cleanup
        audio.onerror = () => { fireStart(); cleanup() }
        audio.play()
          .then(() => fireStart())   // audio is actually playing — safe to reveal text
          .catch(err => {
            console.warn('[Janet TTS] audio.play() blocked:', err?.message)
            fireStart()              // blocked (e.g. autoplay policy) — reveal text anyway
            cleanup()
          })
      })
    }).catch(() => { fireStart(); /* swallow chain errors */ })
  }, [fetchTTS])

  // ── Public: streaming chunk API ─────────────────────────────────────────────
  // Append `delta` to the buffer; flush every completed sentence to TTS.
  const speakChunk = useCallback((delta) => {
    if (!delta) return
    sentenceBufferRef.current += delta
    const [complete, rest] = pullCompleteSentences(sentenceBufferRef.current)
    if (complete) {
      sentenceBufferRef.current = rest
      for (const s of splitSentences(complete)) enqueueSentence(s)
    } else if (sentenceBufferRef.current.length > 60) {
      // No sentence boundary yet but buffer is long — flush at last word boundary
      // so TTS starts quickly instead of waiting for the full sentence.
      const buf = sentenceBufferRef.current
      const lastSpace = buf.lastIndexOf(' ')
      if (lastSpace > 20) {
        sentenceBufferRef.current = buf.slice(lastSpace + 1)
        enqueueSentence(buf.slice(0, lastSpace))
      }
    }
  }, [enqueueSentence])

  // Flush remaining partial sentence (if any) and wait for full playback drain.
  const flushSpeak = useCallback(async () => {
    const tail = sentenceBufferRef.current.trim()
    sentenceBufferRef.current = ''
    if (tail) enqueueSentence(tail)
    // Safety ceiling: never block longer than 15s total (covers slow Cartesia + long audio)
    try {
      await Promise.race([
        playbackChainRef.current,
        new Promise(resolve => setTimeout(resolve, 15000)),
      ])
    } catch { /* drained with errors */ }
    setIsSpeaking(false)
  }, [enqueueSentence])

  // ── Public: legacy one-shot API (still used elsewhere if any) ───────────────
  const speak = useCallback(async (text) => {
    if (!text?.trim()) return
    stopCurrent()
    // Only speak first 2 sentences — rest is already visible as text
    const sentences = splitSentences(text).slice(0, 2)
    for (const s of sentences) enqueueSentence(s)
    try { await playbackChainRef.current } catch { /* drained with errors */ }
    setIsSpeaking(false)
  }, [stopCurrent, enqueueSentence])

  // ── Public: single-utterance API for voiceMode ───────────────────────────────
  // Sends the complete text as ONE Cartesia request so the model generates
  // natural prosody across the whole response — no sentence-boundary gaps,
  // no intonation break when a statement ends and a question follows.
  //
  // onAudioStart (optional): fired when audio.play() resolves so the caller
  // can reveal text in sync with the first sound rather than before it.
  const speakFull = useCallback((text, onAudioStart) => {
    if (!text?.trim()) return
    stopCurrent()
    enqueueSentence(text.trim(), onAudioStart)
    setIsSpeaking(true)
  }, [stopCurrent, enqueueSentence])

  return {
    voiceEnabled,
    toggleVoice,
    speak,
    speakChunk,
    speakFull,
    flushSpeak,
    stop: stopCurrent,
    isSpeaking,
  }
}
